import {
  Component, Input, OnChanges, OnDestroy,
  SimpleChanges, ViewChild, ElementRef, AfterViewInit, inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  Chart, registerables, ChartConfiguration, ChartType as CJType, TooltipItem,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Subscription } from 'rxjs';
import { ThemeService } from '../themes/theme.service';
import { ThemeTokens } from '../themes/theme.model';

Chart.register(...registerables, ChartDataLabels);

// ── Tipos ──────────────────────────────────────────────────────────────────────
export type ChartJsType =
  | 'cj_bar'       // barras verticales
  | 'cj_bar_h'     // barras horizontales
  | 'cj_stacked'   // barras apiladas
  | 'cj_line'      // línea con área
  | 'cj_area'      // área multi-serie
  | 'cj_combo'     // barras + línea %
  | 'cj_donut'     // donut
  | 'cj_pie'       // pie
  | 'cj_radar'     // radar / araña
  | 'cj_polar'     // polar area
  | 'cj_bubble'    // burbuja
  | 'cj_scatter';  // dispersión

/** Info de la query fuente — aparece en el tooltip ℹ de la card */
export interface CjQueryInfo {
  cardId:      number;
  description: string;
  fields:      string[];
}

export interface ChartJsCardConfig {
  title:      string;
  chartType:  ChartJsType;
  data:       Record<string, any>[];
  labelKey?:  string;
  valueKey?:  string;
  value2Key?: string;
  barColor?: string;
  barBorderColor?: string;
  comboStyle?: 'solid' | 'soft';
  hideYTicks?: boolean;
  hideY2Ticks?: boolean;
  size?: 'sm' | 'md' | 'tall';
  queryInfo?: CjQueryInfo;
}

// ── Helpers ────────────────────────────────────────────────────────────────────
const fmtCO = (v: number) => Number(v).toLocaleString('es-CO');
const fmtK  = (v: number) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : String(Math.round(v));
const wrapLabel = (label: string, maxLength = 22, maxLines = 3): string[] => {
  const words = String(label).split(' ');
  const lines: string[] = [];
  let currentLine = '';
  words.forEach(word => {
    if ((currentLine + word).length > maxLength) {
      if (currentLine) lines.push(currentLine.trim());
      currentLine = word + ' ';
    } else {
      currentLine += word + ' ';
    }
  });
  if (currentLine) lines.push(currentLine.trim());
  return lines.slice(0, maxLines);
};

// ── Nombre legible por tipo ────────────────────────────────────────────────────
const TYPE_NAMES: Record<ChartJsType, string> = {
  cj_bar:     'Barras verticales',
  cj_bar_h:   'Barras horizontales',
  cj_stacked: 'Barras apiladas',
  cj_line:    'Línea + área',
  cj_area:    'Área multi-serie',
  cj_combo:   'Combo barras + línea %',
  cj_donut:   'Donut',
  cj_pie:     'Pie',
  cj_radar:   'Radar',
  cj_polar:   'Polar area',
  cj_bubble:  'Burbuja',
  cj_scatter: 'Dispersión',
};

// ─────────────────────────────────────────────────────────────────────────────

@Component({
  selector: 'app-chartjs-card',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './chartjs-card.component.html',
  styleUrl:    './chartjs-card.component.scss',
})
export class ChartjsCardComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() config!: ChartJsCardConfig;
  @Input() loading = false;
  @Input() size?: 'sm' | 'md' | 'tall';
  @ViewChild('canvas') canvasRef!: ElementRef<HTMLCanvasElement>;

  private chart:    Chart | null = null;
  private viewReady  = false;
  private themeSub!: Subscription;
  private theme!:    ThemeTokens;

  private readonly themeSvc = inject(ThemeService);

  showQueryInfo = false;

  // ── Getters del tema ─────────────────────────────────────────────────────────
  private get p(): string[] { return this.theme?.palette ?? ['#6366f1','#38bdf8','#34d399','#fb923c','#f472b6','#a78bfa','#facc15','#2dd4bf']; }
  private get gridColor():  string { return this.theme?.chartGrid ?? 'rgba(0,0,0,0.06)'; }
  private get axisColor():  string { return this.theme?.chartAxis  ?? '#64748b'; }
  private get tooltipBg():  string { return this.theme?.chartTooltipBg   ?? '#1e293b'; }
  private get tooltipFg():  string { return this.theme?.chartTooltipText ?? '#fff'; }

  get typeName(): string { return TYPE_NAMES[this.config?.chartType] ?? ''; }

  // ── Lifecycle ────────────────────────────────────────────────────────────────
  ngAfterViewInit(): void {
    this.viewReady = true;

    this.themeSub = this.themeSvc.theme$.subscribe(t => {
      this.theme = t;
      if (this.config?.data?.length) setTimeout(() => this.build());
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['loading'] && !this.loading && this.config?.data?.length && this.viewReady)
      setTimeout(() => this.build());
    if (changes['config'] && !this.loading && this.viewReady && this.config?.data?.length)
      setTimeout(() => this.build());
  }

  ngOnDestroy(): void {
    this.themeSub?.unsubscribe();
    this.chart?.destroy();
  }

  // ── Build ────────────────────────────────────────────────────────────────────
  private build(): void {
    this.chart?.destroy();
    const ctx = this.canvasRef?.nativeElement?.getContext('2d');
    if (!ctx) return;
    this.chart = new Chart(ctx, this.getConfig());
  }

  private getConfig(): ChartConfiguration {
    switch (this.config.chartType) {
      case 'cj_combo':   return this.comboConfig();
      case 'cj_bar':     return this.barConfig();
      case 'cj_bar_h':   return this.barHConfig();
      case 'cj_stacked': return this.stackedConfig();
      case 'cj_line':    return this.lineConfig();
      case 'cj_area':    return this.areaConfig();
      case 'cj_donut':   return this.donutConfig();
      case 'cj_pie':     return this.pieConfig();
      case 'cj_radar':   return this.radarConfig();
      case 'cj_polar':   return this.polarConfig();
      case 'cj_bubble':  return this.bubbleConfig();
      case 'cj_scatter': return this.scatterConfig();
      default:           return { type: 'bar', data: { labels: [], datasets: [] } };
    }
  }

  // ── Opciones base reutilizables ───────────────────────────────────────────────
  private baseScales(xRotation = 45) {
    return {
      x: {
        ticks: { maxRotation: xRotation, font: { size: 10 }, color: this.axisColor },
        grid:  { color: this.gridColor },
      },
      y: {
        ticks: {
          display: this.config?.hideYTicks === false,
          font: { size: 10 },
          color: this.axisColor,
          callback: (v: any) => fmtK(Number(v)),
        },
        grid:  { color: this.gridColor },
      },
    };
  }

  private basePlugins(showLegend = true) {
    return {
      legend: {
        display: showLegend,
        position: 'top' as const,
        labels: { font: { size: 11 }, boxWidth: 14, color: this.axisColor },
      },
      tooltip: {
        enabled: false,
        backgroundColor: this.tooltipBg,
        titleColor: this.tooltipFg,
        bodyColor: this.tooltipFg,
        padding: 10,
        cornerRadius: 8,
      },
      datalabels: { display: false },
    };
  }

  // ════════════════════════════════════════════════════════════════════════
  // 1. COMBO: barras OBJ + Ejec + línea %
  // ════════════════════════════════════════════════════════════════════════
  private comboConfig(): ChartConfiguration {
    const { data, labelKey, valueKey, value2Key } = this.config;
    const hasPct  = data.length > 0 && 'Porcentaje_CUMP' in data[0];
    const labels  = data.map(r => String(r[labelKey!]).substring(0, 18));
    const lineColor = '#1e293b';
    const style = this.config.comboStyle ?? 'solid';
    const ejecColor = '#d9468f'; // rosa fuerte, no neón
    const objColor = '#1e3a8a';  // azul oscuro
    const ejecBg = style === 'soft' ? `${ejecColor}99` : `${ejecColor}cc`;
    const objBg = style === 'soft' ? `${objColor}99` : `${objColor}cc`;
    const barRadius = style === 'soft' ? 8 : 4;

    const barLabelCfg = {
      display: true,
      anchor: 'center' as const,
      align: 'center' as const,
      color: '#ffffff',
      font: { size: 9, weight: 'bold' as const },
      formatter: (v: number) => fmtK(v),
    };

    const datasets: any[] = [];
    if (value2Key) {
      // Siempre primero Objetivos
      datasets.push({
        type: 'bar', label: 'Objetivos',
        data: data.map(r => Number(r[value2Key])),
        backgroundColor: objBg, borderColor: objColor,
        borderWidth: 1, borderRadius: barRadius,
        datalabels: barLabelCfg,
      });
    }
    // Luego Ejecuciones
    datasets.push({
      type: 'bar', label: 'Ejecuciones',
      data: data.map(r => Number(r[valueKey!])),
      backgroundColor: ejecBg, borderColor: ejecColor,
      borderWidth: 1, borderRadius: barRadius,
      datalabels: barLabelCfg,
    });
    if (hasPct) datasets.push({
      type: 'line', label: '% Cumplimiento',
      data: data.map(r => +(Number(r['Porcentaje_CUMP']) * 100).toFixed(1)),
      borderColor: lineColor, backgroundColor: 'transparent',
      borderWidth: 3, pointRadius: 5, pointHoverRadius: 7,
      pointBackgroundColor: lineColor, pointBorderColor: '#fff', pointBorderWidth: 2,
      tension: 0.35, yAxisID: 'y1', order: 0,
      datalabels: {
        display: true, align: 'top', anchor: 'end',
        color: '#fff', font: { size: 10, weight: 'bold' as const },
        backgroundColor: lineColor,
        borderRadius: 4, padding: { top: 2, bottom: 2, left: 5, right: 5 },
        formatter: (v: number) => `${v}%`,
      },
    });

    return {
      type: 'bar',
      data: { labels, datasets },
      options: {
        responsive: true, maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        scales: {
          x: this.baseScales().x,
          y: this.baseScales().y,
          ...(hasPct ? {
            y1: {
              position: 'right' as const, min: 0, max: 120,
              grid: { drawOnChartArea: false },
              ticks: {
                display: this.config?.hideY2Ticks === false,
                font: { size: 10 },
                color: lineColor,
                callback: (v: any) => `${v}%`,
              },
            },
          } : {}),
        },
        plugins: {
          ...this.basePlugins(),
          tooltip: {
            ...this.basePlugins().tooltip,
            callbacks: {
              label: (ctx: TooltipItem<CJType>) => {
                const v = ctx.parsed.y ?? 0;
                return ctx.dataset.label!.includes('%') ? `${ctx.dataset.label}: ${v}%` : `${ctx.dataset.label}: ${fmtCO(v)}`;
              },
            },
          },
        },
      },
    };
  }

  // ════════════════════════════════════════════════════════════════════════
  // 2. BARRAS VERTICALES
  // ════════════════════════════════════════════════════════════════════════
  private barConfig(): ChartConfiguration {
    const { data, labelKey, valueKey, value2Key } = this.config;
    const hasPct = data.length > 0 && 'Porcentaje_CUMP' in data[0];
    const pal    = this.p;
    const barColor = this.config.barColor;
    const barBorderColor = this.config.barBorderColor ?? barColor;

    const datasets: any[] = [{
      label: valueKey ?? 'Valor',
      data: data.map(r => Number(r[valueKey!])),
      backgroundColor: barColor ? barColor : data.map((_, i) => pal[i % pal.length] + 'cc'),
      borderColor:     barBorderColor ? barBorderColor : data.map((_, i) => pal[i % pal.length]),
      borderWidth: 1, borderRadius: 5,
      datalabels: {
        display: true, anchor: 'end', align: 'top',
        font: { size: 10, weight: 'bold' as const }, color: this.axisColor,
        formatter: (v: number, ctx: any) => {
          if (hasPct) {
            const pct = data[ctx.dataIndex]?.['Porcentaje_CUMP'];
            return pct !== undefined ? `${(Number(pct) * 100).toFixed(0)}%` : fmtK(v);
          }
          return fmtK(v);
        },
      },
    }];
    if (value2Key) datasets.push({
      label: value2Key,
      data: data.map(r => Number(r[value2Key])),
      backgroundColor: this.p[2] + '88', borderColor: this.p[2],
      borderWidth: 1, borderRadius: 5, datalabels: { display: false },
    });

    return {
      type: 'bar',
      data: { labels: data.map(r => String(r[labelKey!]).substring(0, 18)), datasets },
      options: {
        responsive: true, maintainAspectRatio: false,
        layout: { padding: { top: 24 } },
        scales: this.baseScales(40),
        plugins: {
          ...this.basePlugins(!!value2Key),
          tooltip: {
            ...this.basePlugins().tooltip,
            callbacks: {
              label: (ctx: TooltipItem<CJType>) => {
                const row = data[ctx.dataIndex];
                const pct = row?.['Porcentaje_CUMP'];
                const base = `${ctx.dataset.label}: ${fmtCO(ctx.parsed.y ?? 0)}`;
                return pct !== undefined ? `${base}  (${(Number(pct) * 100).toFixed(1)}%)` : base;
              },
            },
          },
        },
      },
    };
  }

  // ════════════════════════════════════════════════════════════════════════
  // 3. BARRAS HORIZONTALES
  // ════════════════════════════════════════════════════════════════════════
  private barHConfig(): ChartConfiguration {
    const { data, labelKey, valueKey, value2Key } = this.config;
    const hasPct  = data.length > 0 && 'Porcentaje_CUMP' in data[0];
    const c1      = this.config.barColor        ?? (this.p[0] + 'bb');
    const c1b     = this.config.barBorderColor  ?? this.p[0];
    const c2      = this.config.barColor ? '#1e3a8acc' : (this.p[2] + '88');
    const c2b     = this.config.barColor ? '#1e3a8a'   : this.p[2];
    const lineColor = '#1e293b';

    const datasets: any[] = [];

    // Primero Objetivos (value2Key)
    if (value2Key) {
      datasets.push({
        label: value2Key,
        data: data.map(r => Number(r[value2Key])),
        backgroundColor: c2, borderColor: c2b,
        borderWidth: 1, borderRadius: 4,
        datalabels: {
          display: true, anchor: 'center', align: 'center',
          font: { size: 9, weight: 'bold' as const }, color: '#fff',
          formatter: (v: number) => fmtK(v),
        },
      });
    }

    // Segundo Ejecuciones (valueKey)
    datasets.push({
      label: valueKey ?? 'Ejecución',
      data: data.map(r => Number(r[valueKey!])),
      backgroundColor: c1, borderColor: c1b,
      borderWidth: 1, borderRadius: 4,
      datalabels: {
        display: true, anchor: 'center', align: 'center',
        font: { size: 9, weight: 'bold' as const }, color: '#fff',
        formatter: (v: number) => fmtK(v),
      },
    });
    if (hasPct) datasets.push({
      type: 'line', label: '% Cumplimiento',
      data: data.map(r => +(Number(r['Porcentaje_CUMP']) * 100).toFixed(1)),
      xAxisID: 'x1',
      borderColor: lineColor, backgroundColor: 'transparent',
      borderWidth: 3, pointRadius: 5, pointHoverRadius: 7,
      pointBackgroundColor: lineColor, pointBorderColor: '#fff', pointBorderWidth: 2,
      tension: 0.2, order: 0,
      datalabels: {
        display: true, align: 'right', anchor: 'end',
        color: '#fff', font: { size: 10, weight: 'bold' as const },
        backgroundColor: lineColor,
        borderRadius: 4, padding: { top: 2, bottom: 2, left: 5, right: 5 },
        formatter: (v: number) => `${v}%`,
      },
    });

    return {
      type: 'bar',
      data: { labels: data.map(r => wrapLabel(r[labelKey!], 24, 3)), datasets },
      options: {
        indexAxis: 'y' as const,
        responsive: true, maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        layout: { padding: { right: 48 } },
        scales: {
          x: { ticks: { font: { size: 10 }, color: this.axisColor, callback: (v: any) => fmtK(Number(v)) }, grid: { color: this.gridColor } },
          y: { ticks: { font: { size: 10 }, color: this.axisColor }, grid: { color: this.gridColor } },
          ...(hasPct ? {
            x1: {
              position: 'top' as const, min: 0, max: 120,
              grid: { drawOnChartArea: false },
              ticks: {
                display: this.config?.hideY2Ticks === false,
                font: { size: 10 }, color: lineColor,
                callback: (v: any) => `${v}%`,
              },
            },
          } : {}),
        },
        plugins: {
          ...this.basePlugins(),
          tooltip: {
            ...this.basePlugins().tooltip,
            callbacks: {
              label: (ctx: TooltipItem<CJType>) => {
                const row = data[ctx.dataIndex];
                const pct = row?.['Porcentaje_CUMP'];
                if (ctx.dataset.label?.includes('%'))
                  return `% Cumplimiento: ${(Number(pct) * 100).toFixed(1)}%`;
                return `${ctx.dataset.label}: ${fmtCO(ctx.parsed.x ?? 0)}`;
              },
            },
          },
        },
      },
    };
  }

  // ════════════════════════════════════════════════════════════════════════
  // 4. BARRAS APILADAS
  // ════════════════════════════════════════════════════════════════════════
  private stackedConfig(): ChartConfiguration {
    const { data, labelKey, valueKey, value2Key } = this.config;

    return {
      type: 'bar',
      data: {
        labels: data.map(r => String(r[labelKey!]).substring(0, 18)),
        datasets: [
          {
            label: valueKey ?? 'Ejecución',
            data: data.map(r => Number(r[valueKey!])),
            backgroundColor: this.p[0] + 'cc', borderColor: this.p[0], borderWidth: 1,
            datalabels: {
              display: true, color: '#fff', font: { size: 9, weight: 'bold' as const },
              formatter: fmtK,
            },
          },
          ...(value2Key ? [{
            label: value2Key,
            data: data.map(r => Math.max(0, Number(r[value2Key]) - Number(r[valueKey!]))),
            backgroundColor: this.p[1] + 'aa', borderColor: this.p[1], borderWidth: 1,
            datalabels: {
              display: true, color: '#fff', font: { size: 9, weight: 'bold' as const },
              formatter: fmtK,
            },
          }] : []),
        ],
      },
      options: {
        responsive: true, maintainAspectRatio: false,
        scales: {
          x: { stacked: true, ...this.baseScales(40).x },
          y: { stacked: true, ...this.baseScales().y },
        },
        plugins: this.basePlugins(),
      },
    };
  }

  // ════════════════════════════════════════════════════════════════════════
  // 5. LÍNEA CON ÁREA
  // ════════════════════════════════════════════════════════════════════════
  private lineConfig(): ChartConfiguration {
    const { data, labelKey, valueKey, value2Key } = this.config;

    const datasets: any[] = [{
      label: valueKey ?? 'Valor',
      data: data.map(r => Number(r[valueKey!])),
      borderColor: this.p[0], backgroundColor: this.p[0] + '25',
      borderWidth: 2.5, fill: true, tension: 0.4,
      pointRadius: 4, pointBackgroundColor: this.p[0],
      datalabels: {
        display: true, align: 'top', anchor: 'end',
        font: { size: 10, weight: 'bold' as const }, color: this.p[0],
        formatter: fmtK,
      },
    }];
    if (value2Key) datasets.push({
      label: value2Key,
      data: data.map(r => Number(r[value2Key])),
      borderColor: this.p[3], backgroundColor: this.p[3] + '18',
      borderWidth: 2, fill: true, tension: 0.4,
      pointRadius: 3, pointBackgroundColor: this.p[3],
      datalabels: { display: false },
    });

    return {
      type: 'line',
      data: { labels: data.map(r => r[labelKey!]), datasets },
      options: {
        responsive: true, maintainAspectRatio: false,
        layout: { padding: { top: 24 } },
        interaction: { mode: 'index', intersect: false },
        scales: this.baseScales(0),
        plugins: {
          ...this.basePlugins(),
          tooltip: {
            ...this.basePlugins().tooltip,
            callbacks: { label: (ctx: TooltipItem<CJType>) => `${ctx.dataset.label}: ${fmtCO(ctx.parsed.y ?? 0)}` },
          },
        },
      },
    };
  }

  // ════════════════════════════════════════════════════════════════════════
  // 6. ÁREA MULTI-SERIE (sin labels encima para no solapar)
  // ════════════════════════════════════════════════════════════════════════
  private areaConfig(): ChartConfiguration {
    const { data, labelKey, valueKey, value2Key } = this.config;

    return {
      type: 'line',
      data: {
        labels: data.map(r => r[labelKey!]),
        datasets: [
          {
            label: valueKey ?? 'Serie 1',
            data: data.map(r => Number(r[valueKey!])),
            borderColor: this.p[0], backgroundColor: this.p[0] + '44',
            borderWidth: 2, fill: true, tension: 0.35, pointRadius: 3,
            datalabels: { display: false },
          },
          ...(value2Key ? [{
            label: value2Key,
            data: data.map(r => Number(r[value2Key])),
            borderColor: this.p[1], backgroundColor: this.p[1] + '33',
            borderWidth: 2, fill: true, tension: 0.35, pointRadius: 3,
            datalabels: { display: false },
          }] : []),
        ],
      },
      options: {
        responsive: true, maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        scales: this.baseScales(0),
        plugins: {
          ...this.basePlugins(),
          tooltip: {
            ...this.basePlugins().tooltip,
            callbacks: { label: (ctx: TooltipItem<CJType>) => `${ctx.dataset.label}: ${fmtCO(ctx.parsed.y ?? 0)}` },
          },
        },
      },
    };
  }

  // ════════════════════════════════════════════════════════════════════════
  // 7. DONUT
  // ════════════════════════════════════════════════════════════════════════
  private donutConfig(): ChartConfiguration<'doughnut'> {
    const { data, labelKey, valueKey } = this.config;
    const total = data.reduce((s, r) => s + Number(r[valueKey!]), 0);
    const pal   = this.p;

    return {
      type: 'doughnut',
      data: {
        labels: data.map(r => String(r[labelKey!]).substring(0, 22)),
        datasets: [{
          data: data.map(r => Number(r[valueKey!])),
          backgroundColor: pal.map(c => c + 'dd'),
          borderColor: pal, borderWidth: 2, hoverOffset: 10,
          datalabels: {
            display: true, color: '#fff',
            font: { size: 10, weight: 'bold' as const },
            formatter: (v: number) => `${((v / total) * 100).toFixed(1)}%`,
          } as any,
        }],
      },
      options: {
        responsive: true, maintainAspectRatio: false, cutout: '62%',
        plugins: {
          legend: { position: 'bottom', labels: { font: { size: 10 }, boxWidth: 12, padding: 10, color: this.axisColor } },
          tooltip: {
            backgroundColor: this.tooltipBg, titleColor: this.tooltipFg, bodyColor: this.tooltipFg,
            callbacks: { label: (ctx: TooltipItem<'doughnut'>) => `${ctx.label}: ${fmtCO(ctx.parsed)}  (${((ctx.parsed / total) * 100).toFixed(1)}%)` },
          },
          datalabels: {},
        },
      },
    };
  }

  // ════════════════════════════════════════════════════════════════════════
  // 8. PIE
  // ════════════════════════════════════════════════════════════════════════
  private pieConfig(): ChartConfiguration<'pie'> {
    const { data, labelKey, valueKey } = this.config;
    const total = data.reduce((s, r) => s + Number(r[valueKey!]), 0);
    const pal   = this.p;

    return {
      type: 'pie',
      data: {
        labels: data.map(r => String(r[labelKey!]).substring(0, 22)),
        datasets: [{
          data: data.map(r => Number(r[valueKey!])),
          backgroundColor: pal.map(c => c + 'ee'),
          borderColor: '#fff', borderWidth: 2, hoverOffset: 8,
          datalabels: {
            display: true, color: '#fff',
            font: { size: 10, weight: 'bold' as const },
            formatter: (v: number) => `${((v / total) * 100).toFixed(1)}%`,
          } as any,
        }],
      },
      options: {
        responsive: true, maintainAspectRatio: false,
        plugins: {
          legend: { position: 'bottom', labels: { font: { size: 10 }, boxWidth: 12, padding: 10, color: this.axisColor } },
          tooltip: {
            backgroundColor: this.tooltipBg, titleColor: this.tooltipFg, bodyColor: this.tooltipFg,
            callbacks: { label: (ctx: TooltipItem<'pie'>) => `${ctx.label}: ${fmtCO(ctx.parsed)}  (${((ctx.parsed / total) * 100).toFixed(1)}%)` },
          },
          datalabels: {},
        },
      },
    };
  }

  // ════════════════════════════════════════════════════════════════════════
  // 9. RADAR
  // ════════════════════════════════════════════════════════════════════════
  private radarConfig(): ChartConfiguration {
    const { data, labelKey, valueKey } = this.config;

    return {
      type: 'radar',
      data: {
        labels: data.map(r => String(r[labelKey!]).substring(0, 16)),
        datasets: [{
          label: valueKey ?? 'Valor',
          data: data.map(r => Number(r[valueKey!])),
          backgroundColor: this.p[0] + '33', borderColor: this.p[0],
          borderWidth: 2, pointBackgroundColor: this.p[0], pointRadius: 4,
          datalabels: {
            display: true, color: this.p[0],
            font: { size: 10, weight: 'bold' as const }, formatter: fmtK,
          } as any,
        }],
      },
      options: {
        responsive: true, maintainAspectRatio: false,
        scales: {
          r: {
            ticks: { font: { size: 9 }, color: this.axisColor, callback: (v: any) => fmtK(Number(v)) },
            pointLabels: { font: { size: 10 }, color: this.axisColor },
            grid: { color: this.gridColor },
            angleLines: { color: this.gridColor },
          },
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: this.tooltipBg, titleColor: this.tooltipFg, bodyColor: this.tooltipFg,
            callbacks: { label: (ctx: TooltipItem<'radar'>) => `${ctx.dataset.label}: ${fmtCO(ctx.parsed.r ?? 0)}` },
          },
          datalabels: {},
        },
      },
    };
  }

  // ════════════════════════════════════════════════════════════════════════
  // 10. POLAR AREA
  // ════════════════════════════════════════════════════════════════════════
  private polarConfig(): ChartConfiguration<'polarArea'> {
    const { data, labelKey, valueKey } = this.config;
    const pal = this.p;

    return {
      type: 'polarArea',
      data: {
        labels: data.map(r => String(r[labelKey!]).substring(0, 20)),
        datasets: [{
          data: data.map(r => Number(r[valueKey!])),
          backgroundColor: pal.map(c => c + 'bb'),
          borderColor: pal, borderWidth: 1.5,
          datalabels: {
            display: true, color: '#fff',
            font: { size: 10, weight: 'bold' as const }, formatter: fmtK,
          } as any,
        }],
      },
      options: {
        responsive: true, maintainAspectRatio: false,
        scales: {
          r: {
            ticks: { font: { size: 9 }, color: this.axisColor, backdropColor: 'transparent' },
            grid: { color: this.gridColor },
          },
        },
        plugins: {
          legend: { position: 'bottom', labels: { font: { size: 10 }, boxWidth: 12, padding: 10, color: this.axisColor } },
          tooltip: {
            backgroundColor: this.tooltipBg, titleColor: this.tooltipFg, bodyColor: this.tooltipFg,
            callbacks: { label: (ctx: TooltipItem<'polarArea'>) => `${ctx.label}: ${fmtCO(ctx.parsed.r ?? 0)}` },
          },
          datalabels: {},
        },
      },
    };
  }

  // ════════════════════════════════════════════════════════════════════════
  // 11. BUBBLE
  // ════════════════════════════════════════════════════════════════════════
  private bubbleConfig(): ChartConfiguration<'bubble'> {
    const { data, labelKey, valueKey, value2Key } = this.config;
    const maxR = Math.max(...data.map(r => Number(r[value2Key ?? valueKey!]))) || 1;

    return {
      type: 'bubble',
      data: {
        datasets: [{
          label: valueKey ?? 'Valor',
          data: data.map((r, i) => ({
            x: i + 1,
            y: Number(r[valueKey!]),
            r: Math.max(4, (Number(r[value2Key ?? valueKey!]) / maxR) * 30),
          })),
          backgroundColor: this.p.map(c => c + 'aa'),
          borderColor: this.p,
          borderWidth: 1.5,
          datalabels: { display: false } as any,
        }],
      },
      options: {
        responsive: true, maintainAspectRatio: false,
        scales: {
          x: {
            ticks: { font: { size: 10 }, color: this.axisColor,
              callback: (_v: string | number, i: number) => String(data[i]?.[labelKey!] ?? '').substring(0, 12) },
            grid: { color: this.gridColor },
          },
          y: { ticks: { font: { size: 10 }, color: this.axisColor, callback: (v: any) => fmtK(Number(v)) }, grid: { color: this.gridColor } },
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: this.tooltipBg, titleColor: this.tooltipFg, bodyColor: this.tooltipFg,
            callbacks: {
              label: (ctx: TooltipItem<'bubble'>) => {
                const r = data[ctx.dataIndex];
                return `${r?.[labelKey!] ?? ctx.dataIndex + 1}: ${fmtCO(ctx.parsed.y ?? 0)}`;
              },
            },
          },
          datalabels: {},
        },
      },
    };
  }

  // ════════════════════════════════════════════════════════════════════════
  // 12. SCATTER (dispersión OBJ vs Ejec)
  // ════════════════════════════════════════════════════════════════════════
  private scatterConfig(): ChartConfiguration<'scatter'> {
    const { data, labelKey, valueKey, value2Key } = this.config;

    return {
      type: 'scatter',
      data: {
        datasets: [{
          label: `${valueKey} vs ${value2Key}`,
          data: data.map(r => ({ x: Number(r[valueKey!]), y: Number(r[value2Key!]) })),
          backgroundColor: this.p.map((c, i) => data[i] ? c + 'cc' : c + 'cc'),
          borderColor: this.p[0], borderWidth: 1.5,
          pointRadius: 7, pointHoverRadius: 10,
          datalabels: { display: false } as any,
        }],
      },
      options: {
        responsive: true, maintainAspectRatio: false,
        scales: {
          x: { title: { display: true, text: valueKey ?? 'X', font: { size: 11 }, color: this.axisColor },
               ticks: { font: { size: 10 }, color: this.axisColor, callback: (v: any) => fmtK(Number(v)) }, grid: { color: this.gridColor } },
          y: { title: { display: true, text: value2Key ?? 'Y', font: { size: 11 }, color: this.axisColor },
               ticks: { font: { size: 10 }, color: this.axisColor, callback: (v: any) => fmtK(Number(v)) }, grid: { color: this.gridColor } },
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: this.tooltipBg, titleColor: this.tooltipFg, bodyColor: this.tooltipFg,
            callbacks: {
              title: (items: TooltipItem<'scatter'>[]) => String(data[items[0].dataIndex]?.[labelKey!] ?? ''),
              label: (ctx: TooltipItem<'scatter'>) =>
                [`${valueKey}: ${fmtCO(ctx.parsed.x ?? 0)}`, `${value2Key}: ${fmtCO(ctx.parsed.y ?? 0)}`],
            },
          },
          datalabels: {},
        },
      },
    };
  }
}
